/**
 * Total XP a learner needs to hold `rank`. Everyone starts at Rank 0 with no XP; each rank costs
 * 100 XP more than the one before it (100, 300, 600, 1000, …), so early ranks come within a
 * city's quests and later ones take several cities.
 */
export function xpForRank(rank: number): number {
  return 50 * rank * (rank + 1);
}

/** The highest rank whose XP requirement `xp` meets. */
export function rankForXp(xp: number): number {
  // Inverts `xpForRank`; the loops absorb floating-point error at exact thresholds.
  let rank = Math.max(0, Math.floor((Math.sqrt(1 + (4 * Math.max(0, xp)) / 50) - 1) / 2));
  while (xpForRank(rank + 1) <= xp) {
    rank += 1;
  }
  while (rank > 0 && xpForRank(rank) > xp) {
    rank -= 1;
  }
  return rank;
}

export interface RankProgress {
  rank: number;
  /** XP earned since reaching `rank`. */
  xpIntoRank: number;
  /** XP between `rank` and the next one. */
  xpPerRank: number;
}

/** Where `xp` sits between its rank and the next, for a progress bar. */
export function rankProgressForXp(xp: number): RankProgress {
  const rank = rankForXp(xp);
  return {
    rank,
    xpIntoRank: Math.max(0, xp) - xpForRank(rank),
    xpPerRank: xpForRank(rank + 1) - xpForRank(rank),
  };
}
